import { Component, VERSION } from '@angular/core';
import { MatTableDataSource } from '@angular/material/table';
import { MatDialog } from '@angular/material/dialog';
import { HttpClient } from '@angular/common/http';
import { ToasterService } from '../services/toaster/toaster.service';
import { HttpService } from '../services/http/http.service';
import { UiService } from '../services/uiloader/uiloader.service';
import { CommonService } from '../services/common/common.service';
import { ThemeService } from '../services/theme/theme.service';
import readXlsxFile from 'read-excel-file';
import writeXlsxFile from 'write-excel-file';
import { config } from '../config';

interface UserData {
  firstName: any;
  lastName: any;
  email: any;
  gender?: string;
  birthDate?: string;
  mobileNumber?: string;
  address?: string;
  aptSuite?: string;
  city?: string;
  state?: string;
  postalCode?: string;
  isValid?: boolean;
}
const schema = {
  'First Name': {
    prop: 'firstName',
    type: String,
    required: true,
  },
  'Last Name': {
    prop: 'lastName',
    type: String,
    required: true,
  },
  'Email': {
    prop: 'email',
    type: String,
    required: true,
  },
  Gender: {
    prop: 'gender',
    type: String,
  },
  BirthDate: {
    prop: 'birthDate',
    type: String,
  },
  'Mobile Number': {
    prop: 'mobileNumber',
    type: String,
  },
  Address: {
    prop: 'address',
    type: String,
  },
  'Apt Suite': {
    prop: 'aptSuite',
    type: String,
  },
  City: {
    prop: 'city',
    type: String,
  },
  State: {
    prop: 'state',
    type: String,
  },
  'Postal Code': {
    prop: 'postalCode',
    type: String,
  },
};

@Component({
  selector: 'app-user-bulk-upload',
  templateUrl: './user-bulk-upload.component.html',
  styleUrls: ['./user-bulk-upload.component.sass']
})
export class UserBulkUploadComponent {
  displayedColumns: string[] = [
    'firstName',
    'lastName',
    'email',
    'gender',
    'birthDate',
    'mobileNumber',
    'address',
    'aptSuite',
    'city',
    'state',
    'postalCode',
  ];
  validDataSource = new MatTableDataSource<UserData>([]);
  invalidDataSource = new MatTableDataSource<UserData>([]);

  isSubmitting = false;

  constructor(
    public dialog: MatDialog,
    private http: HttpClient,
    private toasterService: ToasterService,
    private httpService: HttpService,
    private loader: UiService,
    private common: CommonService,
    public themeService: ThemeService
  ) {}

  // Function to generate and download sample template using write-excel-file
  async downloadTemplate() {
    const schema = [
      { column: 'First Name', type: String, value: () => '' },
      { column: 'Last Name', type: String, value: () => '' },
      { column: 'Email', type: String, value: () => '' },
      { column: 'Gender', type: String, value: () => '' },
      { column: 'BirthDate', type: String, value: () => '' },
      { column: 'Mobile Number', type: String, value: () => '' },
      { column: 'Address', type: String, value: () => '' },
      { column: 'Apt Suite', type: String, value: () => '' },
      { column: 'City', type: String, value: () => '' },
      { column: 'State', type: String, value: () => '' },
      { column: 'Postal Code', type: String, value: () => '' },
    ];

    const data:any = [];
    await writeXlsxFile(data, { schema, fileName: 'sample_template.xlsx' });
  }

  async downloadInvalidData(invalidDataSource:any){
    const schema = [
      { column: 'First Name', type: String, value: (entry:any) => entry.firstName },
      { column: 'Last Name', type: String, value: (entry:any) => entry.lastName },
      { column: 'Email', type: String, value: (entry:any) => entry.email },
      { column: 'Gender', type: String, value: (entry:any) => entry.gender },
      { column: 'BirthDate', type: String, value: (entry:any) => entry.birthDate },
      { column: 'Mobile Number', type: String, value: (entry:any) => entry.mobileNumber },
      { column: 'Address', type: String, value: (entry:any) => entry.address },
      { column: 'Apt Suite', type: String, value: (entry:any) => entry.aptSuite },
      { column: 'City', type: String, value: (entry:any) => entry.city },
      { column: 'State', type: String, value: (entry:any) => entry.state },
      { column: 'Postal Code', type: String, value: (entry:any) => entry.postalCode },
    ];
    await writeXlsxFile(invalidDataSource, { schema, fileName: 'invalid_file.xlsx' });
  }

  // Function to handle file upload
  onFileChange(event: any) {
    const file = event.target.files[0];
    if (!file) return;

    // Clear existing data before processing the new file
    this.validDataSource.data = [];
    this.invalidDataSource.data = [];

    readXlsxFile(file, { schema })
      .then(({ rows, errors }) => {
        // if (errors.length > 0) {
        //   alert('Error reading file: ' + JSON.stringify(errors));
        // }

        if (rows.length > 100) {
          this.toasterService.show('warning', 'Warning', 'Maximum 100 records are allowed.');
          // Reset the file input to allow trying again
          const fileInput = document.getElementById('getFile') as HTMLInputElement;
          if (fileInput) {
            fileInput.value = '';
          }
          return;
        }

        let validData: UserData[] = [];
        let invalidData: UserData[] = [];

        rows.forEach((row) => {
          let isValid = row.firstName && row.lastName && row.email;
          if (isValid) {
            validData.push({
              ...row,
              firstName: row.firstName,
              lastName: row.lastName,
              email: row.email,
            });
          } else {
            invalidData.push({
              ...row,
              firstName: row.firstName,
              lastName: row.lastName,
              email: row.email,
            });
          }
        });

        this.validDataSource.data = validData;
        this.invalidDataSource.data = invalidData;
        
        if (validData.length > 0 || invalidData.length > 0) {
          this.toasterService.show('info', 'File Processed', 
            `File uploaded successfully. Found ${validData.length} valid and ${invalidData.length} invalid records.`);
        } else {
          this.toasterService.show('warning', 'Empty File', 'The uploaded file contains no data.');
        }
      })
      .catch((error) => {
        console.log(error);
        this.toasterService.show('error', 'Error', 'Error reading file. Please check the format and try again.');
        // Reset the file input to allow trying again
        const fileInput = document.getElementById('getFile') as HTMLInputElement;
        if (fileInput) {
          fileInput.value = '';
        }
      });
  }
  
  // Function to clear all data
  clearData() {
    // Reset the data sources
    this.validDataSource.data = [];
    this.invalidDataSource.data = [];
    
    // Reset the file input element to allow uploading the same file again
    const fileInput = document.getElementById('getFile') as HTMLInputElement;
    if (fileInput) {
      fileInput.value = '';
    }
    
    this.toasterService.show('info', 'Cleared', 'All uploaded data has been cleared. You can now upload a new file.');
  }

  // Function to submit valid data to API
  submitToApi() {
    if (this.validDataSource.data.length === 0) {
      this.toasterService.show('warning', 'Warning', 'No valid data to submit.');
      return;
    }
    
    this.loader.showSpinner();
    this.isSubmitting = true;
    
    // Extract emails from valid data
    const emails = this.validDataSource.data.map(user => user.email);
    
    // First API call to create bulk clients
    const createUrl = `${config.apiUrl}/createbulkclients`;
    this.httpService.postCall(createUrl, { emails }).subscribe(
      data => {
        const { success, created_clients, failed_clients, existing_emails } = data;
        
        if (created_clients && created_clients.length > 0) {
          // Prepare clients data for update
          const clients = created_clients.map((client: any) => {
            // Find the corresponding user data from our valid data source
            const userData = this.validDataSource.data.find(user => user.email === client.email);
            
            if (userData) {
              return {
                user_id: client.user_id,
                email: userData.email,
                firstname: userData.firstName,
                lastname: userData.lastName,
                gender: userData.gender || '',
                birthdate: userData.birthDate || '',
                mobilephone: userData.mobileNumber || '',
                address: userData.address || '',
                aptsuite: userData.aptSuite || '',
                city: userData.city || '',
                state: userData.state || '',
                postalcode: userData.postalCode || ''
              };
            }
            return null;
          }).filter((client:any) => client !== null);
          
          // Second API call to update bulk clients
          const updateUrl = `${config.apiUrl}/updateBulkClients`;
          this.httpService.putCall(updateUrl, { clients }).subscribe(
            updateData => {
              this.loader.stopSpinner();
              this.isSubmitting = false;
              this.toasterService.show('success', 'Success', `Successfully created ${created_clients.length} clients`);
              
              // Clear the data after successful upload
              this.validDataSource.data = [];
              this.invalidDataSource.data = [];
              
              // Handle failed clients if needed
              if (failed_clients && failed_clients.length > 0) {
                this.toasterService.show('warning', 'Warning', `${failed_clients.length} clients failed to create`);
              }
              
              // Handle existing emails if needed
              if (existing_emails && existing_emails.length > 0) {
                this.toasterService.show('warning', 'Info', `${existing_emails.length} emails already exist`);
              }
            },
            err => {
              this.loader.stopSpinner();
              this.isSubmitting = false;
              const msg = this.common.getFormattedError(err);
              this.toasterService.show('error', 'Error Occurred', msg);
              console.error('API Error:', err);
            }
          );
        } else {
          this.loader.stopSpinner();
          this.isSubmitting = false;
          this.toasterService.show('warning', 'Warning', 'No clients were created');
        }
      },
      err => {
        this.loader.stopSpinner();
        this.isSubmitting = false;
        const msg = this.common.getFormattedError(err);
        this.toasterService.show('error', 'Error Occurred', msg);
        console.error('API Error:', err);
      }
    );
  }
}
